import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { debicheckAPI, DebiCheckMandateStatus } from '../../services/api';
import './MandateManagement.css';

type MandateListItem = Awaited<
  ReturnType<typeof debicheckAPI.adminListMandates>
>['data']['mandates'][number];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending (NuPay)' },
  { value: 'authenticated', label: 'Authorised' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'error', label: 'Error' },
];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusToneClass(status: DebiCheckMandateStatus): string {
  switch (status) {
    case 'authenticated':
      return 'mandate-badge mandate-badge--success';
    case 'declined':
    case 'expired':
      return 'mandate-badge mandate-badge--warning';
    case 'error':
      return 'mandate-badge mandate-badge--danger';
    case 'cancelled':
      return 'mandate-badge mandate-badge--neutral';
    default:
      return 'mandate-badge mandate-badge--info';
  }
}

const MandateManagement: React.FC = () => {
  const [mandates, setMandates] = useState<MandateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<MandateListItem | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listRes = await debicheckAPI.adminListMandates({
        status: status || undefined,
        search: search || undefined,
      });
      if (listRes.success) {
        setMandates(listRes.data.mandates);
      } else {
        setError('Failed to load mandates');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load mandates');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (m: MandateListItem) => {
    setSelected(m);
    setDetail(null);
    setDetailLoading(true);
    setActionMessage(null);
    try {
      const res = await debicheckAPI.adminGetMandateDetail(m.id);
      if (res.success) setDetail(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load mandate detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const handleAuthoriseMandate = async () => {
    if (!selected) return;
    const nupayReference =
      window.prompt(
        'Optional: paste NuPay / bank mandate reference (or leave blank):',
        selected.mandateReference || ''
      ) ?? null;
    if (nupayReference === null) return; // cancelled prompt

    if (
      !window.confirm(
        `Mark mandate for ${selected.user.firstName} ${selected.user.lastName} as authorised?\n\nOnly do this after NuPay shows bank approval.`
      )
    ) {
      return;
    }

    try {
      setActionBusy(true);
      const res = await debicheckAPI.adminAuthoriseMandate(selected.id, {
        nupayReference: nupayReference.trim() || undefined,
      });
      if (res.success) {
        setActionMessage(res.message || 'Mandate authorised. Student notified.');
        await load();
        closeDetail();
      } else {
        setActionMessage('Authorise failed. Please try again.');
      }
    } catch (e: any) {
      setActionMessage(e?.response?.data?.message || 'Authorise failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancelMandate = async () => {
    if (!selected) return;
    if (!window.confirm(`Cancel mandate ${selected.accountReference}? This will unlink pending payments and cannot be undone from the app.`)) {
      return;
    }
    try {
      setActionBusy(true);
      const res = await debicheckAPI.cancelMandate(selected.id);
      if (res.success) {
        setActionMessage('Mandate cancelled. Refreshing list…');
        await load();
        closeDetail();
      } else {
        setActionMessage('Cancel failed. Please try again.');
      }
    } catch (e: any) {
      setActionMessage(e?.response?.data?.message || 'Cancel failed');
    } finally {
      setActionBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    mandates.forEach((m) => {
      c[m.status] = (c[m.status] || 0) + 1;
    });
    return c;
  }, [mandates]);

  return (
    <div className="mandate-management">
      <div className="mandate-management-header">
        <div>
          <h2>DebiCheck mandates</h2>
          <p className="mandate-management-subtitle">
            NuPay manual setup · student submits bank details → you register on
            NuPay → mark Authorised here after bank approval
          </p>
        </div>
        <div className="mandate-management-stats">
          <div className="stat-pill">
            <span className="stat-pill-label">Authorised</span>
            <span className="stat-pill-value">{counts.authenticated || 0}</span>
          </div>
          <div className="stat-pill">
            <span className="stat-pill-label">Pending NuPay</span>
            <span className="stat-pill-value">{counts.pending || 0}</span>
          </div>
          <div className="stat-pill">
            <span className="stat-pill-label">Failed</span>
            <span className="stat-pill-value">
              {(counts.declined || 0) + (counts.expired || 0) + (counts.error || 0)}
            </span>
          </div>
        </div>
      </div>

      <div className="mandate-management-toolbar">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="mandate-filter-select"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search by name, phone, loan or reference"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') load();
          }}
          className="mandate-search-input"
        />

        <button className="btn-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Search'}
        </button>
      </div>

      {error && <div className="mandate-error-banner">{error}</div>}

      <div className="mandate-table-wrapper">
        <table className="mandate-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Loan</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Reference</th>
              <th>Submitted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="mandate-empty">Loading mandates…</td>
              </tr>
            )}
            {!loading && mandates.length === 0 && (
              <tr>
                <td colSpan={7} className="mandate-empty">
                  No mandates yet. When a student sets up a debit order, they'll appear here.
                </td>
              </tr>
            )}
            {!loading &&
              mandates.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="mandate-cell-primary">
                      {m.user.firstName} {m.user.lastName}
                    </div>
                    <div className="mandate-cell-secondary">{m.user.phoneNumber}</div>
                  </td>
                  <td>
                    <div className="mandate-cell-primary">{m.loan.loanNumber}</div>
                    <div className="mandate-cell-secondary">
                      Total {formatCurrency(m.loan.totalAmount)}
                    </div>
                  </td>
                  <td>{formatCurrency(m.collectionAmount)}</td>
                  <td>
                    <span className={statusToneClass(m.status)}>{m.status}</span>
                  </td>
                  <td>
                    <div className="mandate-cell-secondary mandate-cell-ref">
                      {m.mandateReference || <em>Not returned yet</em>}
                    </div>
                    <div className="mandate-cell-secondary">{m.accountReference}</div>
                  </td>
                  <td>{formatDate(m.createdAt)}</td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={() => openDetail(m)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="mandate-detail-overlay" onClick={closeDetail}>
          <div className="mandate-detail-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="mandate-detail-close"
              onClick={closeDetail}
              aria-label="Close"
            >
              ×
            </button>

            <h3>Mandate {selected.accountReference}</h3>
            <p className="mandate-cell-secondary">
              {selected.user.firstName} {selected.user.lastName} · Loan {selected.loan.loanNumber}
            </p>

            {actionMessage && (
              <div className="mandate-action-message">{actionMessage}</div>
            )}

            <div className="mandate-detail-grid">
              <div>
                <span className="mandate-detail-label">Status</span>
                <span className={statusToneClass(selected.status)}>{selected.status}</span>
              </div>
              <div>
                <span className="mandate-detail-label">NuPay / mandate reference</span>
                <code>{selected.mandateReference || 'Not set yet'}</code>
              </div>
              <div>
                <span className="mandate-detail-label">Collection amount</span>
                <span>{formatCurrency(selected.collectionAmount)}</span>
              </div>
              <div>
                <span className="mandate-detail-label">First collection</span>
                <span>{formatDate(selected.firstCollectionDate)}</span>
              </div>
              <div>
                <span className="mandate-detail-label">Collection day</span>
                <span>{selected.collectionDayCode || '—'}</span>
              </div>
              <div>
                <span className="mandate-detail-label">Bank</span>
                <span>
                  {selected.bankAccountName || '—'} · branch {selected.branchCode || '—'}
                </span>
              </div>
              <div>
                <span className="mandate-detail-label">Authenticated at</span>
                <span>{formatDate(selected.authenticatedAt)}</span>
              </div>
              <div>
                <span className="mandate-detail-label">Last traced</span>
                <span>{formatDate(selected.lastTracedAt)}</span>
              </div>
            </div>

            {(selected.errorCode || selected.errorMessage) && (
              <div className="mandate-detail-error">
                <strong>Error</strong>
                <p>{selected.errorMessage}</p>
                {selected.errorCode && (
                  <p className="mandate-cell-secondary">
                    Code: <code>{selected.errorCode}</code>
                  </p>
                )}
              </div>
            )}

            {detailLoading && <p>Loading details…</p>}
            {detail && (
              <details className="mandate-detail-payloads">
                <summary>Setup payload (support debug)</summary>
                <div className="mandate-detail-payload-block">
                  <h4>Student submission</h4>
                  <pre>
                    {JSON.stringify(detail.netcashRequestPayload, null, 2) || 'null'}
                  </pre>
                </div>
                <div className="mandate-detail-payload-block">
                  <h4>Authorisation record</h4>
                  <pre>
                    {JSON.stringify(detail.netcashResponsePayload, null, 2) || 'null'}
                  </pre>
                </div>
              </details>
            )}

            {detail && (detail.bankAccountNumber || selected.bankAccountName) && (
              <div className="mandate-detail-error" style={{ background: '#f8fafc', borderColor: '#cbd5e1' }}>
                <strong>Copy into NuPay</strong>
                <p>
                  Holder: {detail.bankAccountName || selected.bankAccountName || '—'}
                  <br />
                  Account: {detail.bankAccountNumber || '—'}
                  <br />
                  Branch: {detail.branchCode || selected.branchCode || '—'}
                  <br />
                  Type: {detail.bankAccountType || '—'}
                  <br />
                  Phone: {detail.mobileNumber || selected.user.phoneNumber}
                  <br />
                  ID: {detail.debtorIdNumber || '—'}
                </p>
              </div>
            )}

            <div className="mandate-detail-actions">
              {selected.status === 'pending' && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAuthoriseMandate}
                  disabled={actionBusy}
                >
                  {actionBusy ? 'Saving…' : 'Mark as authorised'}
                </button>
              )}
              {['pending', 'authenticated', 'rms_registered'].includes(selected.status) && (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleCancelMandate}
                  disabled={actionBusy}
                >
                  {actionBusy ? 'Cancelling…' : 'Cancel mandate'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MandateManagement;
